'use client';

import Link from 'next/link';

import { Badge } from '@/components/ui/badge';
import { useTranslation } from '@/lib/i18n/context';

const IncentiveTermsPage = () => {
  const { t, locale } = useTranslation();
  const isAr = locale === 'ar';

  return (
    <section className="py-32">
      <div className="container max-w-4xl">
        <div className="mb-16 flex flex-col gap-6">
          <Badge variant="outline" className="w-fit text-black">
            {t.footer['Legal']}
          </Badge>
          <h1 className="font-serif text-4xl text-black md:text-5xl">
            {t.incentiveTerms['Incentive Campaign Terms']}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t.legal['Last updated:']}{' '}
            {isAr ? '٣٠ ديسمبر ٢٠٢٥' : 'December 30, 2025'}
          </p>
        </div>

        <div className="prose prose-sm prose-slate dark:prose-invert max-w-none text-black">
          <p>
            {
              t.incentiveTerms[
                'These terms ("Terms") apply to the Open marketing incentive campaign described below (the "Offer"). By participating in the Offer, you agree to be bound by these Terms and Open\'s'
              ]
            }
            {isAr ? '' : ' '}
            <Link href="/terms" className="text-primary hover:underline">
              {t.legal['Terms of Service']}
            </Link>
            .{' '}
            {
              t.incentiveTerms[
                'This Offer is administered by Open ("Open," "we," or "us").'
              ]
            }
          </p>

          <h2>{t.incentiveTerms['1. Offer eligibility']}</h2>
          <p>
            {
              t.incentiveTerms[
                'To be eligible to receive the incentive described in these Terms (the "Reward"), you must meet all of the following conditions:'
              ]
            }
          </p>
          <ul>
            <li>
              <strong>{t.incentiveTerms['New to Open:']}</strong>{' '}
              {
                t.incentiveTerms[
                  "You must not be a current or former Open customer, have previously submitted your email to Open, or have previously engaged with Open's sales team."
                ]
              }
            </li>
            <li>
              <strong>{t.incentiveTerms['Session-based activity:']}</strong>{' '}
              {
                t.incentiveTerms[
                  'You must visit at least eight (8) unique pages on the Open website (https://www.open.cx) in a single browsing session.'
                ]
              }
            </li>
            <li>
              <strong>
                {t.incentiveTerms['Session-defined eligibility:']}
              </strong>{' '}
              {
                t.incentiveTerms[
                  'Eligibility is triggered when you reach the eighth unique page during that session. If your session ends before reaching the eighth page, you will not qualify. Eligibility ends when your session ends (e.g., browser tab closed, session times out).'
                ]
              }
            </li>
            <li>
              <strong>{t.incentiveTerms['Geography and role:']}</strong>{' '}
              {
                t.incentiveTerms[
                  'You must (i) be located in a jurisdiction where Open offers its services and (ii) be in a customer support, operations, or leadership role where you have influence over purchasing decisions for your organization.'
                ]
              }
            </li>
            <li>
              <strong>{t.incentiveTerms['Sales meeting:']}</strong>{' '}
              {
                t.incentiveTerms[
                  'After reaching eligibility, you must complete a meeting with an Open sales representative to receive the Reward.'
                ]
              }
            </li>
            <li>
              <strong>{t.incentiveTerms['Identification:']}</strong>{' '}
              {
                t.incentiveTerms[
                  'You must not browse anonymously (i.e., "non-revealed" users are not eligible).'
                ]
              }
            </li>
          </ul>
          <p>
            {
              t.incentiveTerms[
                'Open reserves the right to determine whether you meet the eligibility criteria and may disqualify individuals at its sole discretion.'
              ]
            }
          </p>

          <h2>{t.incentiveTerms['2. Reward fulfillment']}</h2>
          <p>
            {
              t.incentiveTerms[
                'Eligible participants who complete all requirements will receive a $50 digital gift card issued by Open. This card may be used at major retailers including Amazon, Visa, and other participating merchants.'
              ]
            }
          </p>
          <ul>
            <li>
              <strong>{t.incentiveTerms['Delivery:']}</strong>{' '}
              {
                t.incentiveTerms[
                  "Rewards will be delivered by email to eligible participants through Open's gift card fulfillment system."
                ]
              }
            </li>
            <li>
              <strong>{t.incentiveTerms['Timing:']}</strong>{' '}
              {
                t.incentiveTerms[
                  'Rewards for completed meetings will be issued within 5-7 business days of completing your demo.'
                ]
              }
            </li>
            <li>
              <strong>{t.incentiveTerms['Redemption window:']}</strong>{' '}
              {
                t.incentiveTerms[
                  'You must claim your Reward within 30 days of receiving the email. Unclaimed cards will expire.'
                ]
              }
            </li>
            <li>
              <strong>{t.incentiveTerms['Card expiration:']}</strong>{' '}
              {
                t.incentiveTerms[
                  'Once issued, the gift card must be used within one (1) year.'
                ]
              }
            </li>
            <li>
              <strong>{t.incentiveTerms['Tracking:']}</strong>{' '}
              {
                t.incentiveTerms[
                  "Eligible leads are tracked using internal systems, including CRM tagging and campaign enrollment. Reward eligibility and fulfillment are coordinated by Open's Growth team."
                ]
              }
            </li>
          </ul>
          <p>
            {
              t.incentiveTerms[
                'Open is not responsible for late, lost, misdirected, or undelivered emails or for Rewards not received due to user error (including incorrect or inaccessible email addresses or failure to complete required actions).'
              ]
            }
          </p>

          <h2>{t.incentiveTerms['3. Restrictions']}</h2>
          <ul>
            <li>
              <strong>{t.incentiveTerms['Non-transferable:']}</strong>{' '}
              {
                t.incentiveTerms[
                  'Rewards are personal to the recipient and may not be sold, transferred, or assigned.'
                ]
              }
            </li>
            <li>
              <strong>{t.incentiveTerms['No combination:']}</strong>{' '}
              {
                t.incentiveTerms[
                  'This Offer cannot be combined with any other Open offers, referral rewards, promotional campaigns, or discounts.'
                ]
              }
            </li>
            <li>
              <strong>{t.incentiveTerms['One per participant:']}</strong>{' '}
              {t.incentiveTerms['Limit one Reward per eligible participant.']}
            </li>
            <li>
              <strong>{t.incentiveTerms['Tax responsibility:']}</strong>{' '}
              {
                t.incentiveTerms[
                  'Rewards may be taxable. Participants are solely responsible for any applicable taxes and should consult their tax advisor.'
                ]
              }
            </li>
          </ul>

          <h2>{t.incentiveTerms['4. Modifications and termination']}</h2>
          <p>
            {
              t.incentiveTerms[
                'Open reserves the right to modify, suspend, or terminate this Offer at any time without notice and for any reason. Open also reserves the right to withhold or cancel the Reward in the event of actual or suspected fraud, misuse, or violation of these Terms or any applicable law.'
              ]
            }
          </p>
          <p>
            {
              t.incentiveTerms[
                "This Offer is currently scheduled to run beginning December 30, 2025, and may end at any time at Open's discretion."
              ]
            }
          </p>

          <h2>{t.incentiveTerms['5. Limitation of Liability']}</h2>
          <p>
            {
              t.incentiveTerms[
                "Open is not responsible for any errors, delays, or failures in the processing or administration of this offer. All rewards are subject to Open's review and may be withheld or forfeited if eligibility criteria are not met."
              ]
            }
          </p>

          <h2>{t.incentiveTerms['6. Contact Information']}</h2>
          <p>
            {
              t.incentiveTerms[
                'For questions about this Offer or these Terms, please contact us at:'
              ]
            }
            <br />
            {t.legal['Email:']}{' '}
            <a
              href="mailto:legal@open.cx"
              className="text-primary hover:underline"
            >
              legal@open.cx
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default IncentiveTermsPage;
